import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TextField, Typography } from '@mui/material';
import * as api from './businessAreasApi';
import type { BusinessArea } from './businessAreasApi';
import { useDetailView } from '../common/useDetailView';
import { sx14, FormRow, DetailViewShell } from '../common/detailView';

interface Props {
    businessAreaId: number | null;
}

export function BusinessAreaView({ businessAreaId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<BusinessArea>();

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            if (businessAreaId === null) {
                detail.setItem({ id: 0, code: '', name: '' });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getBusinessArea(businessAreaId);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [businessAreaId]);

    useEffect(() => { load(); }, [load]);

    const isCodeValid = !!detail.item?.code && detail.item.code.length <= 20;

    const save = async () => {
        await detail.withSaving(async () => {
            const response = detail.isNew
                ? await api.createBusinessArea(detail.item!)
                : await api.updateBusinessArea(detail.item!);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    const del = async () => {
        if (!window.confirm(t('businessarea.confirmDelete'))) return;
        await detail.withSaving(async () => {
            await api.deleteBusinessArea(detail.item!.id);
            window.history.back();
        });
    };

    return (
        <DetailViewShell<BusinessArea>
            title={`${detail.item?.code ?? ''} ${detail.item?.name ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isCodeValid}
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={del}
            onClearError={() => detail.setError(null)}
        >
            {(item) => (
                <>
                    <FormRow label={t('businessarea.code')}>
                        {!detail.isNew && !detail.isEditing ? (
                            <Typography sx={sx14}>{item.code}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                                value={item.code}
                                onChange={e => detail.update('code', e.target.value.toUpperCase())}
                                error={!isCodeValid} />
                        )}
                    </FormRow>

                    <FormRow label={t('businessarea.name')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{item.name}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                inputProps={{ maxLength: 100 }}
                                value={item.name ?? ''}
                                onChange={e => detail.update('name', e.target.value)} />
                        )}
                    </FormRow>
                </>
            )}
        </DetailViewShell>
    );
}
